import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy, computed, inject, signal } from '@angular/core';
import { Subject, interval, takeUntil, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { UserNotification } from '../models/userNotification';

const REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutos

@Injectable({
  providedIn: 'root',
})
export class NotificationsService implements OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly baseUrl =
    (environment as { api_url?: string }).api_url ?? '';
  private readonly destroy$ = new Subject<void>();
  private initialized = false;

  private readonly _notifications = signal<UserNotification[]>([]);
  private readonly _isLoading = signal(false);
  private readonly _error = signal<string | null>(null);

  readonly notifications = this._notifications.asReadonly();
  readonly isLoading = this._isLoading.asReadonly();
  readonly error = this._error.asReadonly();

  readonly unreadNotifications = computed(() =>
    this._notifications().filter(n => !n.is_read)
  );

  readonly readNotifications = computed(() =>
    this._notifications().filter(n => n.is_read)
  );

  readonly unreadCount = computed(() => this.unreadNotifications().length);

  /**
   * Inicia la carga de notificaciones en segundo plano.
   * Debe llamarse una vez cuando el usuario esté autenticado.
   */
  startPolling(): void {
    if (this.initialized) return;
    this.initialized = true;

    // Carga inicial
    this.refresh();

    // Auto-refresh cada 5 minutos
    interval(REFRESH_INTERVAL_MS)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.refresh());
  }

  /**
   * Fuerza una recarga de las notificaciones.
   */
  refresh(): void {
    this._isLoading.set(true);
    this._error.set(null);

    this.http
      .get<UserNotification[]>(`${this.baseUrl}/notifications/`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: notifications => {
          this._notifications.set(notifications);
          this._isLoading.set(false);
        },
        error: err => {
          console.error('Error al cargar notificaciones:', err);
          this._error.set('No se pudieron cargar las notificaciones');
          this._isLoading.set(false);
        },
      });
  }

  markAsRead(notificationId: string): void {
    this.http
      .patch<void>(`${this.baseUrl}/notifications/${notificationId}/read`, {})
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this._notifications.update(notifications =>
            notifications.map(n =>
              n.id === notificationId ? { ...n, is_read: true } : n
            )
          );
        },
        error: err => {
          console.error('Error al marcar como leída:', err);
        },
      });
  }

  markMultipleAsRead(notificationIds: string[]): void {
    const requests = notificationIds.map(id =>
      this.http.patch<void>(`${this.baseUrl}/notifications/${id}/read`, {})
    );

    Promise.all(requests.map(req => req.toPromise())).then(
      () => {
        const idsSet = new Set(notificationIds);
        this._notifications.update(notifications =>
          notifications.map(n =>
            idsSet.has(n.id) ? { ...n, is_read: true } : n
          )
        );
        this.refresh();
      },
      err => {
        console.error('Error al marcar como leídas:', err);
      }
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
